// Formatting for the System Health screen. Pure, so it is unit-tested.

export const OWNER_LABELS = {
  us: "Our team",
  workshop: "Workshop",
  chat360: "Chat360",
  meta: "WhatsApp",
  none: "Nobody",
};

export const ownerLabel = (owner) => OWNER_LABELS[owner] || "Unknown";

export function formatMs(ms) {
  if (ms == null) return "–";
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)} s` : `${Math.round(ms)} ms`;
}

export function formatSeconds(seconds) {
  if (seconds == null) return "–";
  return seconds < 90 ? `${Math.round(seconds)} s` : `${Math.round(seconds / 60)} min`;
}

export const formatPct = (value) => (value == null ? "–" : `${value}%`);

// What the screen calls each result of a send.
export const RESULT_LABELS = {
  not_sent: "Not sent",
  rejected: "Rejected by Chat360",
  failed: "Failed in WhatsApp",
  delivered: "Delivered",
  read: "Read",
  unconfirmed: "Sent, delivery unknown",
};

export const resultLabel = (result) => RESULT_LABELS[result] || "Unknown";

export const resultTone = (result) =>
  ({ not_sent: "bad", rejected: "bad", failed: "bad", delivered: "good", read: "good" })[result] || "idle";

// What the screen calls each state of a booking's group.
export const OUTCOME_LABELS = { created: "Group created", failed: "Failed", stuck: "Stuck", waiting: "In progress" };

export const outcomeLabel = (outcome) => OUTCOME_LABELS[outcome] || "Unknown";

export const outcomeTone = (outcome) => ({ created: "good", failed: "bad", stuck: "warn" })[outcome] || "idle";

// The four steps of making a group, in order, and what each one means.
export const GROUP_STEPS = [
  { key: "booked", label: "Booked", help: "The booking was complete and saved." },
  { key: "created", label: "Group created", help: "Chat360 was asked and accepted the request." },
  { key: "link", label: "Link received", help: "The group has its invite link." },
  { key: "invites", label: "Invitations sent", help: "Everyone was sent their invitation." },
];

export const stepLabel = (key) => GROUP_STEPS.find((s) => s.key === key)?.label || "";

// What a booking's step can be, in the words the screen uses.
export const STEP_STATE_LABELS = {
  ok: "Done",
  failed: "Failed",
  stuck: "Stuck",
  waiting: "In progress",
  skipped: "Not reached",
  unknown: "No per-person record",
};

export const stepStateLabel = (state) => STEP_STATE_LABELS[state] || "Unknown";

export const stepStateTone = (state) => ({ ok: "good", failed: "bad", stuck: "warn" })[state] || "idle";

// Times are shown on India time, the clock the workshops use.
export function formatWhen(iso) {
  const date = new Date(iso);
  if (!iso || Number.isNaN(date.getTime())) return "–";
  return date.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "numeric",
    month: "short",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

export const STATE_STYLES = {
  good: { dot: "#16A34A", text: "#166534", border: "#E5E7EB", label: "Healthy" },
  warn: { dot: "#D97706", text: "#92400E", border: "#F5D9A8", label: "Watch" },
  bad: { dot: "#DC2626", text: "#991B1B", border: "#F3B4B4", label: "Needs attention" },
  idle: { dot: "#9CA3AF", text: "#4B5563", border: "#E5E7EB", label: "No activity" },
};

export const stateStyle = (state) => STATE_STYLES[state] || STATE_STYLES.idle;

// What the Background jobs screen calls each state of a job.
export const JOB_STATE_LABELS = {
  done: "Sent",
  skipped: "Skipped",
  waiting: "Scheduled",
  retrying: "Will try again",
  running: "Running",
  late: "Late",
  stuck: "Stuck",
  failed: "Failed",
};

export const jobStateLabel = (state) => JOB_STATE_LABELS[state] || "Unknown";

export const jobStateTone = (state) =>
  ({ done: "good", failed: "bad", late: "bad", stuck: "warn", retrying: "warn" })[state] || "idle";

// How long ago a background loop last ran, for "last ran 20 s ago".
export function formatAgo(seconds) {
  if (seconds == null) return "never";
  if (seconds < 90) return `${Math.round(seconds)} s ago`;
  if (seconds < 5400) return `${Math.round(seconds / 60)} min ago`;
  return `${Math.round(seconds / 3600)} h ago`;
}

// When a scheduled job runs, from now: "in 25 min", "in 3 h 10 min", "in 2 days".
// A time already passed reads "any moment now": the runner picks it up within minutes.
export function formatIn(iso, now = Date.now()) {
  const minutes = Math.round((new Date(iso).getTime() - now) / 60000);
  if (!iso || Number.isNaN(minutes)) return "–";
  if (minutes < 1) return "any moment now";
  if (minutes < 60) return `in ${minutes} min`;
  if (minutes < 48 * 60) {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `in ${h} h${m ? ` ${m} min` : ""}`;
  }
  return `in ${Math.round(minutes / 1440)} days`;
}

// A loop is running, not running (it stopped), or has never reported at all.
export const LOOP_STATE_LABELS = { ok: "Running", late: "Not running", never: "Never reported" };

export const loopStateTone = (state) => ({ ok: "good" })[state] || "bad";

// Megabytes as "512 MB" or "7.8 GB".
export function formatSize(mb) {
  if (mb == null) return "–";
  return mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${Math.round(mb)} MB`;
}

// How long something has been running: "12 min", "5 h", "10 days".
export function formatUptime(seconds) {
  if (seconds == null) return "–";
  if (seconds < 3600) return `${Math.max(1, Math.round(seconds / 60))} min`;
  if (seconds < 2 * 86400) return `${Math.round(seconds / 3600)} h`;
  return `${Math.round(seconds / 86400)} days`;
}
